import { useState } from "react"

interface CopyableAddressProps {
    address: string
    /** Show full address or truncate. Default: true (full) */
    full?: boolean
    /** Show ultra-short address (e.g., g174…59c). Overrides `full`. Default: false */
    compact?: boolean
    /** Font size override. Default: 11 */
    fontSize?: number
}

/**
 * Displays a Gno address with 1-click copy.
 * Shows the full address by default; click copies to clipboard.
 */
export function CopyableAddress({ address, full = true, compact = false, fontSize = 11 }: CopyableAddressProps) {
    const [copied, setCopied] = useState(false)

    const display = compact
        ? address.length > 8
            ? `${address.slice(0, 4)}…${address.slice(-3)}`
            : address
        : full
            ? address
            : address.length > 20
                ? `${address.slice(0, 10)}…${address.slice(-8)}`
                : address

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation() // prevent card click bubbling
        navigator.clipboard.writeText(address)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    return (
        <span
            role="button"
            tabIndex={0}
            aria-label={copied ? "Address copied" : `Copy address ${address}`}
            onClick={handleCopy}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleCopy(e as unknown as React.MouseEvent) }}
            title={copied ? "Copied!" : `Click to copy: ${address}`}
            style={{
                fontSize,
                fontFamily: "JetBrains Mono, monospace",
                color: copied ? "var(--color-k-accent-text)" : "var(--color-k-dim)",
                cursor: "pointer",
                wordBreak: "break-all",
                transition: "color 0.15s",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
            }}
        >
            {display}
            <span aria-live="polite" style={{ fontSize: fontSize - 1, opacity: copied ? 1 : 0.4, transition: "opacity 0.15s" }}>
                {copied ? "✓" : "📋"}
            </span>
        </span>
    )
}
